import React, { useContext, useMemo } from 'react'
import { ArticleSearchParams } from '@obeta/models/lib/models/Search'
import { gql } from '@apollo/client'
import { validateContextValue } from '@obeta/utils/lib/validateContextValue'
import { useArticlesSearchProvider } from './useArticleSearchProvider'
import { SearchFacet, SearchFacetElement } from '@obeta/models/lib/schema-models/search'

export interface ArticleSearchFilter {
  suppliers: SearchFacetElement[]
  etimClasses: SearchFacetElement[]
  etimAttributes: SearchFacet[]
  hitCount: number
  listPriceMin: number
  listPriceMax: number
}

export const articleSearchFilterQuery = gql`
  query searchToFilterProducts(
    $searchString: String
    $hitsPerPage: Int
    $filter: String
    $page: Int
    $facets: [String!]
  ) {
    searchProducts(
      searchString: $searchString
      requestOptions: { facets: $facets, hitsPerPage: $hitsPerPage, filters: $filter, page: $page }
    ) {
      nbHits
      nbPages
      facets {
        name
        values {
          value
          count
          meta
        }
        meta
      }
      facetsStats {
        name
        min
        max
      }
      queryId
    }
  }
`

/**
 * using searchParams request and build available etimClasses (classes of features product can have),
 *  etimAttributes (features product can have) and ...
 * @param searchParams
 * @returns
 */
export const useArticleSearchFilter = (searchParams?: ArticleSearchParams | null) => {
  const { searchFilterResult: searchFilter, isFetching } = useArticlesSearchProvider()
  return { searchFilter, loading: isFetching }
}

interface IArticleFilterData {
  articleSearchFilter: ArticleSearchFilter
  normalizedEtimAttributes: Record<string, SearchFacet>
  normalizedSuppliers: Record<string, SearchFacetElement>
  loading: boolean
}

const ArticleFilterContext = React.createContext<IArticleFilterData | null>(null)

export const ArticleFilterProvider: React.FC<{ searchParams: ArticleSearchParams | null }> = (
  props
) => {
  const { searchParams, children } = props
  const { searchFilter: articleFilter, loading } = useArticleSearchFilter(searchParams)

  const data = useMemo(() => {
    const normalizedEtimAttributes = articleFilter.etimAttributes.reduce<
      Record<string, SearchFacet>
    >((acc, etimAttr) => {
      acc[etimAttr.name] = etimAttr
      return acc
    }, {})

    const normalizedSuppliers = articleFilter.suppliers.reduce<Record<string, SearchFacetElement>>(
      (acc, sup) => {
        acc[sup.value] = sup
        return acc
      },
      {}
    )

    return {
      articleSearchFilter: articleFilter,
      normalizedEtimAttributes,
      normalizedSuppliers,
      loading,
    }
  }, [articleFilter, loading])

  return <ArticleFilterContext.Provider value={data}>{children}</ArticleFilterContext.Provider>
}

export const useArticleFilterData = () => {
  return validateContextValue(
    useContext(ArticleFilterContext),
    'ArticleFilterContext',
    'useArticleFilterData'
  )
}
